import { type FC, useEffect, useRef, useState } from 'react';
import { Input, Alert, Tag, Typography } from 'antd';
import { Button } from '@g2u/ui-kit';
import { CloseOutlined, RobotOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { observer } from 'mobx-react-lite';

import logoSmile from 'assets/images/geeks2u-logo-smile.svg';
import logoStarsAndSmile from 'assets/images/geeks2u-logo-stars-and-smile.svg';
import { useAiAssistant, useAiAssistantActions } from 'helpers';
import { CreateTicketModal } from 'components/common/create-ticket-modal';
import { chatActionRegistry } from './chat-actions';
import styles from './ai-assistant.module.scss';

const { Text } = Typography;

export const AiAssistantContent: FC = observer(() => {
  const [inputValue, setInputValue] = useState('');

  const {
    loading,
    conversation,
    errorMsg,
    suggestedChips,
    customerProfile,
    job,
    handlePostQuery,
    handleDismissChip,
    handleClearChat,
  } = useAiAssistant();

  const {
    isTicketModalOpen,
    setIsTicketModalOpen,
    handleCreateTicket,
    handleCallCustomer,
  } = useAiAssistantActions();

  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof chatEndRef.current?.scrollIntoView === 'function') {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation, loading]);

  const handleSubmit = () => {
    const textToSubmit = inputValue.trim();
    if (!textToSubmit) return;
    handlePostQuery(textToSubmit);
    setInputValue('');
  };

  return (
    <div className={styles.aiAssistant}>
      <div className={styles.aiAssistant__header}>
        <div className={styles.aiAssistant__headerTop}>
          <div className={styles.aiAssistant__headerLeft}>
            <img src={logoSmile} alt="Geeks2U Logo" />
            <span>AI Assistant</span>
          </div>
          <Button.$
            dataTestId="clear-chat"
            type={Button.Type.Link}
            className={styles.aiAssistant__headerLink}
            disabled={conversation.length === 0}
            onClick={handleClearChat}
          >
            Clear chat
          </Button.$>
        </div>
        <div className={styles.aiAssistant__subtitle}>
          Helps you find answers from G2U Knowledge Base.
        </div>
      </div>

      <div className={styles.aiAssistant__content}>
        {conversation.length === 0 ? (
          <>
            <div className={styles.aiAssistant__welcome}>
              <img src={logoStarsAndSmile} alt="Geeks2U AI Assistant" className={styles.aiAssistant__welcomeLogo} />
              <h2 className={styles.aiAssistant__welcomeTitle}>Geeks2U AI Assistant</h2>
              <p className={styles.aiAssistant__welcomeGreeting}>Hi, I&apos;m Geeky 👋!</p>
              <p className={styles.aiAssistant__welcomeDescription}>
                I can help you quickly find answers from the Geeks2U Knowledge Base.
              </p>
            </div>

            <div className={styles.aiAssistant__info}>
              <h3 className={styles.aiAssistant__infoTitle}>A few things to keep in mind:</h3>
              <ul className={styles.aiAssistant__infoList}>
                <li>I only have access to approved knowledge base content.</li>
                <li>I can&apos;t view customer personal information, payment details, or other sensitive data.</li>
                <li>I may occasionally provide incomplete or incorrect answers.</li>
                <li>Please double-check important policies, pricing, and procedures before relying on my response.</li>
                <li>When in doubt, consult the relevant documentation or escalate to your team leader.</li>
              </ul>
            </div>
          </>
        ) : (
          <div className={styles.aiAssistant__chatArea}>
            {conversation.map((msg) => {
              const action = msg.action;

              return (
                <div
                  key={msg.id}
                  className={`${styles.aiAssistant__messageRow} ${
                    msg.sender === 'user'
                      ? styles.aiAssistant__messageRow_user
                      : styles.aiAssistant__messageRow_assistant
                  }`}
                >
                  <div
                    className={`${styles.aiAssistant__bubble} ${
                      msg.sender === 'user'
                        ? styles.aiAssistant__bubble_user
                        : styles.aiAssistant__bubble_assistant
                    }`}
                  >
                    {msg.sender === 'user' ? (
                      <Text style={{ color: 'inherit' }}>{msg.text}</Text>
                    ) : (
                      <>
                        <div className="markdown-content" style={{ lineHeight: '1.5' }}>
                          <ReactMarkdown>{msg.text}</ReactMarkdown>
                        </div>

                        {action && action.type && chatActionRegistry[action.type] && (
                          <div className={styles.aiAssistant__actions}>
                            <span className={styles.aiAssistant__actionsTitle}>SUGGESTED ACTION:</span>
                            <div style={{ marginTop: '4px' }}>
                              {chatActionRegistry[action.type].render(action.payload, {
                                onCreateTicket: handleCreateTicket,
                                onCallCustomer: handleCallCustomer,
                              })}
                            </div>
                          </div>
                        )}

                        {msg.sourceLinks && msg.sourceLinks.length > 0 && (
                          <div className={styles.aiAssistant__sources}>
                            <span className={styles.aiAssistant__sourcesTitle}>SOURCES USED:</span>
                            <div style={{ marginTop: '4px' }}>
                              {msg.sourceLinks.map((source, index) => (
                                <Tag key={index} style={{ marginBottom: 4 }}>
                                  <a href={source.url} target="_blank" rel="noopener noreferrer">
                                    {source.title}
                                  </a>
                                </Tag>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div
                className={`${styles.aiAssistant__messageRow} ${styles.aiAssistant__messageRow_assistant}`}
              >
                <div
                  className={`${styles.aiAssistant__bubble} ${styles.aiAssistant__bubble_assistant}`}
                  style={{ color: '#8c8c8c', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <RobotOutlined style={{ fontSize: '18px' }} />
                  <span>Geeky is retrieving resources...</span>
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>
        )}
      </div>

      <div className={styles.aiAssistant__footer}>
        {conversation.length === 0 && suggestedChips.length > 0 && (
          <div className={styles.aiAssistant__suggested}>
            {suggestedChips.map((chip) => (
              <div
                key={chip.id}
                className={styles.aiAssistant__suggestedChip}
                onClick={() => handlePostQuery(chip.text)}
              >
                <span>{chip.text}</span>
                <CloseOutlined onClick={(e) => handleDismissChip(chip.id, e)} />
              </div>
            ))}
          </div>
        )}

        {errorMsg && (
          <Alert
            message="Communication Issue"
            description={errorMsg}
            type="error"
            showIcon
            style={{ marginBottom: '12px' }}
          />
        )}

        <Input.TextArea
          className={styles.aiAssistant__footerInput}
          placeholder="e.g., How do I process a refund?"
          autoSize={{ minRows: 2, maxRows: 6 }}
          value={inputValue}
          disabled={loading}
          onChange={(e) => setInputValue(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey && inputValue.trim()) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <Button.$
          dataTestId="ask-geeky"
          type={Button.Type.Primary}
          className={styles.aiAssistant__footerButton}
          disabled={!inputValue.trim() || loading}
          loading={loading}
          onClick={handleSubmit}
        >
          Ask Geeky
        </Button.$>
      </div>

      <CreateTicketModal
        customerId={customerProfile?.ID}
        jobId={job?.ID}
        isOpen={isTicketModalOpen}
        onClose={() => setIsTicketModalOpen(false)}
      />
    </div>
  );
});
